import React from "react";
//Limitless imports
import { fetchUserAnnouncements, useGraphAPIHook } from "../../../portal";
import { useNotificationContext } from "@limitless/common/hooks/notification/NotificationContext";
import { log as pilog } from "@limitless/common/utils";
import { ACKNOWLEDGE_ANNOUNCEMENT, DISMISS_ANNOUNCEMENT } from "../../../portal/apis/queries/UserAnnouncementQueries";
import { useExpertProfileContext } from "../../hooks/expert-profile/ExpertProfileContext";
const isHighPriority = announcement => announcement.priority === "high" && announcement.state === "unread";
const isNewLowPriority = announcement => announcement.priority === "low" && announcement.state === "unread";
export const useExpertAnnouncements = () => {
  const log = pilog.getLogger("useExpertAnnouncements");
  const [announcements, setAnnouncements] = React.useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = React.useState(null);
  const {
    addFailureNotification,
    addInfoNotification
  } = useNotificationContext();
  const {
    loadInactivatedMemberships
  } = useExpertProfileContext();
  const {
    mutateData
  } = useGraphAPIHook();
  const loadUserAnnouncements = () => {
    fetchUserAnnouncements(response => {
      log.debug({
        fetchAnnouncements: response
      });
      if (response.hasError) {
        addFailureNotification("Failed to retrieve announcements");
      } else {
        const newLPAnnouncements = response.announcements.filter(isNewLowPriority);
        if (newLPAnnouncements.length !== 0) {
          addInfoNotification("You have a new announcement");
        }
        setAnnouncements(response.announcements);
      }
    });
    setTimeout(() => {
      loadInactivatedMemberships();
    }, 1000);
  };
  const addAnnouncement = announcement => {
    const newAnnouncements = [announcement].concat(announcements);
    setAnnouncements(newAnnouncements);
  };
  const removeAnnouncement = announcement => {
    const newAnnouncements = announcements.filter(a => {
      return a.announcementId !== announcement.announcementId;
    });
    setAnnouncements(newAnnouncements);
    setSelectedAnnouncement();
  };
  const updateAnnouncement = announcement => {
    const newAnnouncements = [];
    announcements.map(a => {
      if (a.announcementId === announcement.announcementId) {
        newAnnouncements.push(announcement);
      } else {
        newAnnouncements.push(a);
      }
    });
    setAnnouncements(newAnnouncements);
  };

  // Acknowledge Announcement (delete announcement)
  const acknowledgeAnnouncement = (announcement, callback) => {
    const acknowledgeOptions = {
      dataKey: "dismissAnnouncement",
      mutation: ACKNOWLEDGE_ANNOUNCEMENT,
      variables: {
        announcementId: announcement.announcementId
      },
      functionName: "UserAnnouncementQueries acknowledgeAnnouncement"
    };
    mutateData(acknowledgeOptions).then(() => {
      removeAnnouncement(announcement);
      callback();
    }).catch(() => null);
  };

  // Dismiss Announcement
  const saveAnnouncement = announcement => {
    //optimistically update local copy
    updateAnnouncement(announcement);
    const dismissOptions = {
      dataKey: "dismissAnnouncement",
      mutation: DISMISS_ANNOUNCEMENT,
      variables: {
        announcementId: announcement.announcementId
      },
      functionName: "UserAnnouncementQueries saveAnnouncement"
    };
    mutateData(dismissOptions).catch(() => log.warn(`Failed to save ${announcement.announcementId} announcement`));
  };
  const selectAnnouncement = announcement => {
    //if it's unread, update the platform that it should now be "saved"
    if (announcement.state === "unread") {
      announcement.state = "saved";
      saveAnnouncement(announcement);
    }
    setSelectedAnnouncement(announcement);
  };
  const saveHighPriorityAnnouncement = announcement => {
    announcement.state = "saved";
    announcement.acknowledged = true;
    saveAnnouncement(announcement);
  };
  const getLowPriorityAnnouncements = () => {
    return announcements.filter(a => !isHighPriority(a));
  };
  const getHighPriorityAnnouncements = () => {
    return announcements.filter(a => isHighPriority(a));
  };
  const getNumOfUnreadAnnouncements = () => {
    return getLowPriorityAnnouncements().filter(lpa => lpa.state === "unread").length;
  };
  const clearAnnouncements = () => {
    setAnnouncements([]);
    setSelectedAnnouncement();
  };
  return {
    announcements,
    loadUserAnnouncements,
    getLowPriorityAnnouncements,
    getHighPriorityAnnouncements,
    selectedAnnouncement,
    selectAnnouncement,
    clearAnnouncements,
    addAnnouncement,
    removeAnnouncement,
    acknowledgeAnnouncement,
    getNumOfUnreadAnnouncements,
    saveHighPriorityAnnouncement
  };
};